import type {Dispatch, RefObject, SetStateAction} from 'react';
import {useCallback, useRef, useState} from 'react';
import {useApiStream} from '@Hooks/useApiStream';
import {
  AiChatStreamParams,
  ChatMessage,
  ChatMessagePart,
  ChatMode,
  ChatType,
  ControlStreamEvent,
  FailedMessageStreamEvent,
  ImageStreamEvent,
  MessageRole,
  ProgressStreamEvent,
  StreamDataChunk,
  TextStreamEvent,
  UserMessage,
} from '@Components/ai-chat-window/ai-chat-window.types';
import {
  createStoppedErrorMessage,
  createThinkingMessage,
  deepCopyMessage,
  handleStreamEvent,
  updateMessageWithImageStream,
  updateMessageWithTextStream,
} from '@Components/ai-chat-window/lib/ai-chat-stream.library';
import {getUniqueString} from '@Utils/string.util';
import {noop} from '@Utils/general.util';

interface UseChatStreamProps {
  chatId: string | undefined;
  onChatStart?: () => void;
  setChatId?: (id: string) => void;
  refreshChatHistory?: () => void;
}

export interface UseChatStreamReturn {
  messages: ChatMessage[];
  setMessages: Dispatch<SetStateAction<ChatMessage[]>>;
  isStreaming: boolean;
  setIsStreaming: Dispatch<SetStateAction<boolean>>;
  tempUserMessageRef: RefObject<UserMessage | null>;
  startGenerationStream: (userRequestParts: ChatMessagePart[]) => Promise<void>;
  stopGeneration: () => Promise<void>;
  disconnect: () => void;
  messageSessionUidRef: RefObject<string | null>;
}

export const useChatStream = ({chatId, onChatStart, setChatId, refreshChatHistory}: UseChatStreamProps): UseChatStreamReturn => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const tempUserMessageRef = useRef<UserMessage | null>(null);
  const messageSessionUidRef = useRef<string | null>(null);

  const handleControlEvent = useCallback(
    (event: ControlStreamEvent): void => {
      switch (event.type) {
        case 'chat_id': {
          const lastUnshownUserMessage = tempUserMessageRef.current;

          if (lastUnshownUserMessage) {
            setMessages((prev) => [...prev, lastUnshownUserMessage]);
          }
          setChatId?.(event.data);

          break;
        }
      }
    },
    [setChatId]
  );

  const handleProgressEvent = useCallback((event: ProgressStreamEvent): void => {
    setMessages((prev) => {
      const lastMessage = prev[prev.length - 1];

      if (lastMessage && lastMessage.role === MessageRole.ASSISTANT && lastMessage.isThinking) {
        const updatedMessage = deepCopyMessage(lastMessage);
        updatedMessage.parts = [{type: 'text', text: event.data}];
        return [...prev.slice(0, -1), updatedMessage];
      }

      return [...prev, createThinkingMessage(event.data)];
    });
  }, []);

  const handleTextStreamEvent = useCallback((event: TextStreamEvent): void => {
    setMessages((prev) => {
      const lastMessage = prev[prev.length - 1];
      if (lastMessage.role === MessageRole.ASSISTANT && lastMessage.isThinking) {
        return [...prev.slice(0, -1), event.message];
      }

      if (lastMessage?.role === MessageRole.ASSISTANT) {
        const updatedMessage = updateMessageWithTextStream(lastMessage, event);
        return [...prev.slice(0, -1), updatedMessage];
      }

      return [...prev, event.message];
    });
  }, []);

  const handleImageStreamEvent = useCallback((event: ImageStreamEvent): void => {
    setMessages((prev) => {
      const lastMessage = prev[prev.length - 1];

      if (lastMessage.role === MessageRole.ASSISTANT && lastMessage.isThinking) {
        return [...prev.slice(0, -1), event.message];
      }

      if (lastMessage?.role === MessageRole.ASSISTANT) {
        const updatedMessage = updateMessageWithImageStream(lastMessage, event);
        return [...prev.slice(0, -1), updatedMessage];
      }

      return [...prev, event.message];
    });
  }, []);

  const handleFailedMessageStreamEvent = useCallback((event: FailedMessageStreamEvent): void => {
    setIsStreaming(false);
    const errorMessage = event.message;
    setMessages((prev) => {
      const lastMessage = prev[prev.length - 1];

      if (lastMessage.role === MessageRole.ASSISTANT && lastMessage.isThinking) {
        return [...prev.slice(0, -1), errorMessage];
      }

      return [...prev, errorMessage];
    });
  }, []);

  const handleCloseEvent = useCallback((): void => {
    setMessages((prev) => prev.filter((msg) => msg.role === MessageRole.ASSISTANT && !msg.isThinking));
    setIsStreaming(false);
    console.log('Stream finished.');
  }, []);

  const {connect, disconnect} = useApiStream<StreamDataChunk, AiChatStreamParams>({
    onMessage: (streamEvent) => {
      handleStreamEvent(streamEvent, {
        onControlEvent: handleControlEvent,
        onProgressEvent: handleProgressEvent,
        onTextStreamEvent: handleTextStreamEvent,
        onImageStreamEvent: handleImageStreamEvent,
        onFailedMessageEvent: handleFailedMessageStreamEvent,
        onCloseEvent: handleCloseEvent,
      });
    },
    onClose: noop,
    onError: (error) => {
      console.error('An error occurred in the stream', error);
    },
  });

  const startGenerationStream = useCallback(
    async (userRequestParts: ChatMessagePart[]): Promise<void> => {
      if (messages.length === 0) {
        onChatStart?.();
      }

      const messageSessionUid = getUniqueString();
      messageSessionUidRef.current = messageSessionUid;

      setIsStreaming(true);

      const endpoint = 'ai/messages';

      try {
        await connect(endpoint, {
          chatId,
          mode: ChatMode.CREATE,
          type: ChatType.DESIGN,
          userRequestParts,
          messageSessionUid,
        });
        refreshChatHistory?.();
      } catch (error) {
        console.error('Failed to connect to stream endpoint', error);
      } finally {
        setIsStreaming(false);
        messageSessionUidRef.current = null;
      }
    },
    [chatId, messages.length, connect, onChatStart]
  );

  const stopGeneration = useCallback(async (): Promise<void> => {
    if (!chatId) {
      console.error('Cannot stop generation: No chat ID or message session UID available.');
      return;
    }

    const lastMessage = messages[messages.length - 1];
    let currentStreamContent: ChatMessage | null = null;

    if (isStreaming && lastMessage && lastMessage.role === MessageRole.ASSISTANT && !lastMessage.isThinking) {
      currentStreamContent = lastMessage;
    }

    disconnect();

    try {
      const errorMessage = createStoppedErrorMessage();

      setMessages((prev) => {
        const lastMessage = prev[prev.length - 1];

        if (lastMessage.role === MessageRole.ASSISTANT && lastMessage.isThinking) {
          return [...prev.slice(0, -1), errorMessage];
        }

        return [...prev, errorMessage];
      });

      await window.PMW.writeLocal('ai/stopChat', {
        chatId,
        currentStreamContent: currentStreamContent ?? [],
        messageSessionUid: messageSessionUidRef.current,
      });

      messageSessionUidRef.current = null;

      console.log('Stop signal sent to the server with current stream content.');
    } catch (error) {
      console.error('Failed to send stop signal to the server:', error);
    }
  }, [chatId, messages, disconnect]);

  return {
    messages,
    setMessages,
    isStreaming,
    setIsStreaming,
    tempUserMessageRef,
    startGenerationStream,
    stopGeneration,
    disconnect,
    messageSessionUidRef,
  };
};
